// AI Document Processor Types
// Based on Laravel AiQueue model, AiQueueResource, and related enums

/**
 * Queue Status Enum (matching Laravel AiQueueState)
 * @public
 */
export enum AiQueueStatus {
  Pending = 'pending',
  Processing = 'processing',
  Failed = 'failed',
  Cancelled = 'cancelled',
  AwaitingUserAction = 'awaiting_user_action',
  Completed = 'completed',
}

/**
 * Queue Step Enum (matching Laravel AiQueueStep)
 * @public
 */
export enum AiQueueStep {
  AwaitingAiClassification = 'awaiting_ai_classification',
  AwaitingModelConfirmation = 'awaiting_model_confirmation',
  AwaitingExtraction = 'awaiting_extraction',
  AwaitingAiReview = 'awaiting_ai_review',
  AwaitingUserReview = 'awaiting_user_review',
  AwaitingFinalization = 'awaiting_finalization',
  Completed = 'completed',
  Failed = 'failed',
  Cancelled = 'cancelled',
}

/**
 * Processing Priority Enum (matching Laravel ProcessingPriority)
 * @public
 */
export enum ProcessingPriority {
  Low = 'low',
  Normal = 'normal',
  High = 'high',
}

/**
 * Validation Status Enum (matching Laravel ValidationStatus)
 * @public
 */
export enum ValidationStatus {
  Pending = 'pending',
  Valid = 'valid',
  Invalid = 'invalid',
  PartiallyValid = 'partially_valid',
}

/**
 * File Upload Types
 * @public
 */
export interface UploadableFile {
  file: File
  id: string
  status: 'pending' | 'uploading' | 'completed' | 'error'
  progress: number
  error?: string
}

/**
 * AI Model Configuration
 * @public
 */
export interface AiModel {
  key: string
  name: string
  description: string
  icon: string
}

/**
 * Model Suggestion from AI (extracted from analysis stage results)
 * @public
 */
export interface ModelSuggestion {
  model: string
  confidence: number
  reason: string
}

/**
 * Record created (or matched) by the pipeline, as a CrudPreview reference
 * @public
 */
export interface GeneratedRecord {
  /** Frontend CRUD model name (bill, business_partner, ...) */
  model: string
  /** Record UUID */
  id: string
}

/**
 * Uploaded document file with app-standard media URLs
 * @public
 */
export interface QueueDocument {
  uuid: string
  file_name: string
  mime_type: string | null
  size: number | null
  original_url: string
  thumb_url: string
}

/**
 * Pipeline stage execution summary (matching AiQueueStageResource)
 * @public
 */
export interface QueueStage {
  id: string
  stage: 'analysis' | 'extraction' | 'review' | 'finalize'
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped' | 'cancelled'
  provider_used: string | null
  model_used: string | null
  overall_confidence: number | null
  error_message: string | null
  error_type: string | null
  processing_time_ms: number | null
  retry_count: number
  max_retries: number
  started_at: string | null
  completed_at: string | null
  failed_at: string | null
  created_at: string
}

/**
 * AI Queue Item (matching Laravel AiQueueResource response)
 * @public
 */
export interface AiQueueItem {
  /** UUID identifier */
  id: string
  user_id: number
  /** Current queue state */
  status: AiQueueStatus
  /** Human-readable status label from backend */
  status_label: string
  /** Current processing step (detailed sub-state) */
  queue_step: AiQueueStep | null
  /** Human-readable step label from backend */
  queue_step_label: string | null
  priority: ProcessingPriority
  original_filename: string | null
  mime_type: string | null
  file_size: number | null
  file_hash: string | null
  /** Results from the analysis stage (contains model suggestions) */
  latest_analysis_results: Record<string, any> | null
  /** Results from the extraction stage (contains extracted data) */
  latest_extraction_results: Record<string, any> | null
  /** Results from the review stage (contains confidence scores, validation) */
  latest_review_results: Record<string, any> | null
  /** Results from the finalize stage */
  latest_finalize_results: Record<string, any> | null
  confirmed_model_type: string | null
  processing_errors: Record<string, any> | null
  retry_count: number
  max_retries: number
  /** CrudPreview references to the records the pipeline created */
  generated_records: GeneratedRecord[]
  /** Uploaded files with media URLs */
  documents: QueueDocument[]
  /** Stage executions, only present on the single-item endpoint */
  stages?: QueueStage[]
  /** Processing duration in seconds */
  duration_seconds: number | null
  started_at: string | null
  completed_at: string | null
  failed_at: string | null
  created_at: string
  updated_at: string
}

/**
 * Server-side queue list filters
 * @public
 */
export interface QueueListParams {
  page?: number
  perPage?: number
  status?: AiQueueStatus | 'all'
  priority?: ProcessingPriority | 'all'
  search?: string
}

/**
 * Global queue counts by status (server-side), plus 'all'
 * @public
 */
export type StatusCounts = Record<AiQueueStatus | 'all', number>

/**
 * Progress percentage for each pipeline step, used for table progress bars
 * @public
 */
export const STEP_PROGRESS: Record<AiQueueStep, number> = {
  [AiQueueStep.AwaitingAiClassification]: 10,
  [AiQueueStep.AwaitingModelConfirmation]: 25,
  [AiQueueStep.AwaitingExtraction]: 40,
  [AiQueueStep.AwaitingAiReview]: 60,
  [AiQueueStep.AwaitingUserReview]: 75,
  [AiQueueStep.AwaitingFinalization]: 85,
  [AiQueueStep.Completed]: 100,
  [AiQueueStep.Failed]: 100,
  [AiQueueStep.Cancelled]: 100,
}

/**
 * Statuses considered active for polling cadence purposes
 * @public
 */
export const ACTIVE_STATUSES: AiQueueStatus[] = [AiQueueStatus.Pending, AiQueueStatus.Processing, AiQueueStatus.AwaitingUserAction]

/**
 * Extract model suggestions from a queue item's analysis results
 */
export function getModelSuggestions(item: AiQueueItem): ModelSuggestion[] | null {
  return item.latest_analysis_results?.suggestions ?? item.latest_analysis_results?.model_suggestions ?? null
}

/**
 * Extract extracted data from a queue item's extraction results
 */
export function getExtractedData(item: AiQueueItem): Record<string, any> | null {
  return item.latest_extraction_results?.extracted_data ?? item.latest_extraction_results?.data ?? null
}

/**
 * Extract confidence scores from a queue item's review results
 */
export function getConfidenceScores(item: AiQueueItem): Record<string, number> | null {
  return item.latest_review_results?.confidence_scores ?? null
}

/**
 * API Response Types
 * @public
 */
export interface ApiResponse<T> {
  data: T
  message?: string
  status: 'success' | 'error'
}

/**
 * Paginated API Response
 * @public
 */
export interface PaginatedResponse<T> {
  data: T[]
  current_page: number
  last_page: number
  per_page: number
  total: number
  /** Global counts by status (from list endpoint meta) */
  status_counts?: StatusCounts
}

/**
 * Upload API Request
 * @public
 */
export interface UploadRequest {
  files: File[]
  target_model_class?: string
  confirmed_model_type?: string
  model_config_key?: string
  priority?: ProcessingPriority
  auto_process?: boolean
}

/**
 * Upload API Response
 * @public
 */
export interface UploadResponse {
  queue_id: string
  status: AiQueueStatus
  message: string
}

/**
 * Model Selection Confirmation Request
 * @public
 */
export interface ModelConfirmRequest {
  model_type: string
}

/**
 * Component State Types
 * @public
 */
export interface ProcessingState {
  isUploading: boolean
  uploadProgress: Record<string, number>
  queueItems: AiQueueItem[]
  selectedQueueItem: AiQueueItem | null
  availableModels: AiModel[]
}

/**
 * Component Events
 * @public
 */
export interface AiDocumentProcessorEvents {
  'file-uploaded': [queueId: string]
  'model-confirmed': [queueId: string, modelType: string]
  'processing-cancelled': [queueId: string]
  'queue-updated': [queueItems: AiQueueItem[]]
  'error': [error: string]
}

/**
 * Component Props
 * @public
 */
export interface AiDocumentProcessorProps {
  readonly autoRefresh?: boolean
  readonly refreshInterval?: number
  readonly maxFileSize?: number
  readonly allowedFileTypes?: string[]
  readonly showAdvancedOptions?: boolean
}

/**
 * Confidence Thresholds
 * @public
 */
export interface ConfidenceThresholds {
  high: number
  medium: number
  low: number
}

/**
 * Processing Options
 * @public
 */
export interface ProcessingOptions {
  priority: ProcessingPriority
  autoConfirmHighConfidence: boolean
  confidenceThreshold: number
  enableCaching: boolean
}

/**
 * File Type Configuration
 * @public
 */
export interface FileTypeConfig {
  mimeType: string
  extensions: string[]
  icon: string
  maxSize: number
}

/**
 * Error Types
 * @public
 */
export interface ProcessingError {
  code: string
  message: string
  details?: Record<string, any>
  timestamp: string
}
